import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { handleFeatureNotIncluded } from "../../utils/notifyPlanUpgrade";
import api from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import styles from "./RescheduleBroadcast.module.css";
import { ButtonBack } from "../../components/ui";

const DEFAULT_MESSAGE =
  "Olá! Precisamos reagendar os atendimentos do dia {data}. Por favor, entre em contato conosco para remarcar. Pedimos desculpas pelo transtorno!";

export default function RescheduleBroadcastPage() {
  const { user } = useAuth();
  const [date, setDate] = useState("");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [preview, setPreview] = useState(null); // null | { count, phones }
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const handleDateChange = useCallback(
    async (e) => {
      const val = e.target.value;
      setDate(val);
      setPreview(null);
      if (!val || !user?.tenantId) return;

      setLoading(true);
      try {
        const start = `${val}T00:00:00Z`;
        const end = `${val}T23:59:59Z`;
        const res = await api.get(`/appointments/${user.tenantId}?start=${start}&end=${end}`);
        const apps = res.data ?? [];
        const phones = [...new Set(apps.map((a) => a.clientPhone))];
        setPreview({ count: phones.length, phones });
      } catch {
        toast.error("Erro ao buscar agendamentos para esta data.");
      } finally {
        setLoading(false);
      }
    },
    [user?.tenantId],
  );

  const handleSend = async () => {
    if (!date || !message.trim() || !user?.tenantId) return;
    if (!preview || preview.count === 0) {
      toast.warn("Nenhum cliente para notificar nesta data.");
      return;
    }

    const confirmText = `Enviar mensagem para ${preview.count} cliente(s)?\n\n"${message.slice(0, 100)}..."`;
    if (!window.confirm(confirmText)) return;

    setSending(true);
    try {
      const res = await api.post("/appointments/reschedule-broadcast", {
        tenantId: user.tenantId,
        date,
        message,
      });
      toast.success(`Mensagens enviadas: ${res.data.sent} de ${res.data.total}. Falhas: ${res.data.failed}.`);
      setDate("");
      setMessage(DEFAULT_MESSAGE);
      setPreview(null);
    } catch (err) {
      // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): mensagem
      // humana com CTA, nunca erro cru nem a copy genérica.
      if (!handleFeatureNotIncluded(err)) {
        toast.error("Erro ao enviar as mensagens.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.container}>
      <ButtonBack to="/agenda" />
      <h1 className={styles.title}>Reagendamento em Massa</h1>
      <p className={styles.subtitle}>
        Selecione um dia para notificar todos os clientes com agendamentos confirmados.
      </p>

      <div className={styles.card}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="date">
            Data dos agendamentos
          </label>
          <input
            id="date"
            type="date"
            value={date}
            onChange={handleDateChange}
            className={styles.input}
            disabled={sending}
          />
        </div>

        {loading && <p className={styles.muted}>Verificando agendamentos...</p>}

        {!loading && preview !== null && (
          <div className={preview.count > 0 ? styles.previewBox : styles.previewEmpty}>
            {preview.count > 0 ? (
              <>
                <strong>{preview.count} cliente(s)</strong> serão notificados nesta data.
              </>
            ) : (
              "Nenhum agendamento confirmado encontrado para esta data."
            )}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="message">
            Mensagem
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={styles.textarea}
            rows={5}
            disabled={sending}
            placeholder="Digite a mensagem a ser enviada por WhatsApp..."
          />
        </div>

        <button
          type="button"
          className={styles.sendBtn}
          onClick={handleSend}
          disabled={sending || loading || !date || !preview || preview.count === 0}
        >
          {sending ? "Enviando..." : "Enviar mensagem"}
        </button>
      </div>
    </div>
  );
}
